import { useState } from "react";
import { AlertTriangle, Trash2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export default function DangerZone() {
  const { user, setUser } = useAuth();
  const accountName = user?.account?.name || "";
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [accOpen, setAccOpen] = useState(false);
  const [accStep, setAccStep] = useState(1);
  const [accConfirm, setAccConfirm] = useState("");
  const [accDeleting, setAccDeleting] = useState(false);

  const openDialog = () => {
    setStep(1);
    setConfirmText("");
    setOpen(true);
  };

  const openAccDialog = () => {
    setAccStep(1);
    setAccConfirm("");
    setAccOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/transactions/delete-all", { confirm: confirmText.trim() });
      toast.success(`${data.deleted} işlem kalıcı olarak silindi`);
      setOpen(false);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setAccDeleting(true);
    try {
      const { data } = await api.post("/accounts/delete", { confirm: accConfirm.trim() });
      toast.success(`"${accountName}" hesabı kalıcı olarak kapatıldı`);
      setAccOpen(false);
      setUser(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAccDeleting(false);
    }
  };

  return (
    <div className="bg-card border border-destructive/40 rounded-2xl p-6 space-y-4" data-testid="danger-zone">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-destructive/15 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-destructive" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold text-destructive">Tehlikeli Bölge</h2>
          <p className="text-xs text-muted-foreground">Bu işlemler geri alınamaz — önce yedek almanızı öneririz</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <Button data-testid="delete-all-button" variant="outline" onClick={openDialog}
          className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive">
          <Trash2 className="w-4 h-4 mr-2" /> Tüm Hesap Hareketlerini Sil
        </Button>
        <Button data-testid="delete-account-button" variant="outline" onClick={openAccDialog}
          className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive">
          <XCircle className="w-4 h-4 mr-2" /> Bu Hesabı Kapat
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-all-dialog" className="bg-card border-destructive/40 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {step === 1 ? "Emin misiniz?" : "Son Onay"}
            </DialogTitle>
            <DialogDescription className="sr-only">Tüm işlemleri silme onayı</DialogDescription>
          </DialogHeader>

          {step === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Tüm gelir ve gider kayıtlarınız <span className="text-destructive font-semibold">kalıcı olarak silinecek</span>.
                Bu işlem geri alınamaz. Sabit işlem kuralları ve bütçe ayarlarınız silinmez.
              </p>
              <p className="text-sm text-muted-foreground">
                İpucu: Silmeden önce Ayarlar → Veri Yedekleme'den yedeğinizi indirebilirsiniz.
              </p>
              <div className="flex gap-2 justify-end">
                <Button data-testid="delete-all-cancel-1" variant="ghost" onClick={() => setOpen(false)}
                  className="rounded-full text-muted-foreground">Vazgeç</Button>
                <Button data-testid="delete-all-continue" onClick={() => setStep(2)}
                  className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 font-semibold">
                  Devam Et
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Onaylamak için aşağıdaki kutuya büyük harflerle <span className="text-destructive font-bold">SİL</span> yazın.
              </p>
              <Input data-testid="delete-all-confirm-input" placeholder="SİL" autoFocus
                value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
              <div className="flex gap-2 justify-end">
                <Button data-testid="delete-all-cancel-2" variant="ghost" onClick={() => setOpen(false)}
                  className="rounded-full text-muted-foreground">Vazgeç</Button>
                <Button data-testid="delete-all-confirm-button" onClick={handleDelete}
                  disabled={confirmText.trim() !== "SİL" || deleting}
                  className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 font-semibold">
                  {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Tümünü Kalıcı Olarak Sil
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={accOpen} onOpenChange={setAccOpen}>
        <DialogContent data-testid="delete-account-dialog" className="bg-card border-destructive/40 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              {accStep === 1 ? "Hesabı Kapat" : "Son Onay"}
            </DialogTitle>
            <DialogDescription className="sr-only">Hesabı kapatma onayı</DialogDescription>
          </DialogHeader>

          {accStep === 1 ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-semibold">"{accountName}"</span> hesabı ve içindeki
                tüm işlemler, sabit işlemler, bütçeler, kategoriler, fişler ve üyelikler{" "}
                <span className="text-destructive font-semibold">kalıcı olarak silinecek</span>.
                Üyeler bu hesaba artık erişemeyecek. Bu işlem geri alınamaz.
              </p>
              <p className="text-sm text-muted-foreground">
                Not: Son hesabınızı kapatamazsınız — önce Ayarlar'dan yeni bir hesap oluşturun.
              </p>
              <div className="flex gap-2 justify-end">
                <Button data-testid="delete-account-cancel-1" variant="ghost" onClick={() => setAccOpen(false)}
                  className="rounded-full text-muted-foreground">Vazgeç</Button>
                <Button data-testid="delete-account-continue" onClick={() => setAccStep(2)}
                  className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 font-semibold">
                  Devam Et
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Onaylamak için hesap adını yazın: <span className="text-destructive font-bold">{accountName}</span>
              </p>
              <Input data-testid="delete-account-confirm-input" placeholder={accountName} autoFocus
                value={accConfirm} onChange={(e) => setAccConfirm(e.target.value)} />
              <div className="flex gap-2 justify-end">
                <Button data-testid="delete-account-cancel-2" variant="ghost" onClick={() => setAccOpen(false)}
                  className="rounded-full text-muted-foreground">Vazgeç</Button>
                <Button data-testid="delete-account-confirm-button" onClick={handleDeleteAccount}
                  disabled={accConfirm.trim() !== accountName || accDeleting}
                  className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 font-semibold">
                  {accDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Hesabı Kalıcı Olarak Kapat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
